import {EventEmitter, Injectable, OnDestroy} from '@angular/core';
import {ActivatedRoute, NavigationEnd, NavigationExtras, Params, Router} from '@angular/router';
import {filter, map, pairwise, startWith} from 'rxjs/operators';
import * as localforage from 'localforage';
import {Subscription} from 'rxjs';
import {BlockChangeDetectionService} from '@blocks/services/block-change-detection/block-change-detection.service';
import {Block} from '@paperlessio/sdk/api/models';

@Injectable()
export class EditorRoutingService implements OnDestroy {
  static LAST_MENU_ITEM_KEY = 'last_menu_item';

  private statics: any[];
  private page: number;
  private block: number;
  private blocks: number[];
  private panel: string;
  protected allowedPanelStrings = ['content', 'fields', 'library', 'tokens', 'rules', 'participants', 'localization', 'settings', 'triggers'];
  private previewMode = false;

  changes = new EventEmitter();
  subs = new Subscription();

  constructor(private router: Router,
              private route: ActivatedRoute,
              private blockDetectionService: BlockChangeDetectionService) {
    this.setIVarsFromURL(this.router.url);

    this.subs.add(
      this.router.events.pipe(
        filter(x => x instanceof NavigationEnd)
      ).subscribe((navigationEndEvent: NavigationEnd) => {
        this.setIVarsFromURL(navigationEndEvent.urlAfterRedirects);
      })
    );

    this.subs.add(
      this.changes.pipe(
        map(() => this.block),
        startWith(0), // emitting first empty value to fill in the buffer
        pairwise()
      ).subscribe(([previousValue, currentValue]) => {
        this.blockDetectionService.setChangedBlockIds([previousValue, currentValue]);
      }));

    this.getDeepestChild(this.route).params.subscribe((params: Params) => {
      this.page = params.page;
      this.block = +params.block;
      this.blocks = params.blocks ? params.blocks.split(',').map(x => +x) : [];
    });
  }

  // TODO: Still horrible, but better than before.
  private setIVarsFromURL(url: string) {
    // cut away aux outlets (modals)
    url = url.split('(')[0];

    // cut away query params
    const queryParams = url.split('?')[1];
    url = url.split('?')[0];

    const pathItems: string[] = url.split('/');
    if (pathItems?.length > 2) {
      // We're interested in everything from 'edit' on
      this.statics = pathItems.splice(1, pathItems.indexOf('edit') - 1);

      // in 4 is the panel in document/template mode; in partial mode, the panel is in 2
      const panel = pathItems[4] ?? pathItems[2];

      if (this.allowedPanelStrings.includes(panel) || panel === '') {
        localforage.setItem(EditorRoutingService.LAST_MENU_ITEM_KEY, panel);
      }

      this.panel = panel && this.allowedPanelStrings.includes(panel) ? panel : '';
      // the active panel for a selected block is ''. thus, is the panel is not empty, we shall reset the block id
      if (this.panel !== '') {
        this.block = null;
      }
    }

    if (queryParams) {
      const params = new URLSearchParams(queryParams);
      if (params.has('preview')) {
        this.previewMode = params.get('preview') === 'true';
      }
    }
  };

  get selectedBlock(): number {
    return this.block || -1;
  }

  get selectedBlocks(): number[] {
    return this.blocks || [];
  }

  get selectedPage(): number {
    return this.page || -1;
  }

  get selectedPanel(): string {
    return this.panel || '';
  }

  get inPreviewMode(): boolean {
    return this.previewMode;
  }

  private getDeepestChild(route: ActivatedRoute): ActivatedRoute {
    const child = route.children[route.children.length - 1];
    return child.children.length > 0 ? this.getDeepestChild(child) : child;
  }

  async navigateToBase() {
    this.page = null;
    this.panel = null;
    this.block = null;
    this.blocks = [];
    await this.navigate();
  }

  async navigateToPage(page_id: number, panel = this.panel, replaceUrl = false, keepBlock = false) {
    this.page = +page_id;
    this.panel = panel;
    this.block = keepBlock ? this.block : null;
    this.blocks = [];
    await this.navigate(replaceUrl);
  }

  async navigateToBlock(block: number) {
    this.block = +block;
    this.panel = null;
    this.blocks = [];
    await this.navigate();
  }

  async navigateToBlockSelection(blocks: number[]) {
    this.blocks = blocks;

    if (blocks.length > 0) {
      this.block = null;
      this.panel = 'selection';
      await this.navigate();
    } else {
      await this.navigateToLastActivePanel();
    }
  }

  async navigateToPanel(item: string) {
    this.block = null;
    this.blocks = [];
    this.panel = this.allowedPanelStrings.includes(item) ? item : 'content';
    await this.navigate();
  }

  async togglePreviewMode() {
    this.previewMode = !this.previewMode;
    if (this.previewMode) {
      await this.navigateToPanel('rules');
    } else {
      await this.navigate();
    }
  }

  async navigate(replaceUrl = false) {
    const a = [...this.statics, 'edit'];
    if (this.page) {
      a.push('pages', this.page.toString());
    }
    if (this.block) {
      a.push('blocks', this.block.toString());
    }
    if (this.panel) {
      a.push(this.panel);
    }
    const extras: NavigationExtras = {replaceUrl, queryParamsHandling: 'merge'};
    if (this.previewMode) {
      extras.queryParams = {preview: true};
    } else {
      extras.queryParams = {preview: null};
    }
    this.changes.emit(a);
    await this.router.navigate(a, extras);
  }

  async navigateToLastActivePanel() {
    const lastMenuItem = await localforage.getItem<string>(EditorRoutingService.LAST_MENU_ITEM_KEY);
    await this.navigateToPanel(lastMenuItem);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  blockSelected(b: Block) {
    return b?.id === this.selectedBlock || this.selectedBlocks.includes(b?.id);
  }
}
